import { Injectable, OnDestroy, inject } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { WorkflowStatusEvent } from '../api/models/workflow-status-event';
import { AlertKind, AlertService } from './alert.service';
import { RealtimeNotificationsService } from './realtime-notifications.service';
import { UserService } from './user.service';
import { workflowLabel } from './workflow-descriptors';

const STATUS_KIND: Readonly<Record<WorkflowStatusEvent['status'], AlertKind>> = {
  started: 'info',
  succeeded: 'success',
  failed: 'error',
  cancelled: 'neutral',
  timed_out: 'warning',
};

/**
 * Subscribes to `workflow_status` SSE events for the current user and
 * dispatches each to `AlertService`. The backend already routes by
 * `user_id`, so the in-tab user-id check is purely defensive — a
 * misrouted event should never produce a foreign user's toast.
 *
 * Bootstrapped from `AppComponent` so the subscription begins as soon as
 * the user is authenticated.
 */
@Injectable({
  providedIn: 'root',
})
export class WorkflowStatusToastHandler implements OnDestroy {
  private readonly realtime = inject(RealtimeNotificationsService);
  private readonly alerts = inject(AlertService);
  private readonly userService = inject(UserService);

  private currentUserId: number | null = null;
  private readonly destroyed$ = new Subject<void>();

  public start(): void {
    this.userService.user$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((user) => {
        this.currentUserId = user.userId;
      });

    this.realtime.events$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((event) => {
        if (event.event_type !== 'workflow_status') {
          return;
        }
        this.handle(event);
      });
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private handle(event: WorkflowStatusEvent): void {
    if (this.currentUserId != null && event.user_id !== this.currentUserId) {
      return;
    }
    const label = event.workflow_label || workflowLabel(event.workflow_type);
    const text = this.toastText(event, label);
    this.alerts.show(text, STATUS_KIND[event.status]);
  }

  private toastText(event: WorkflowStatusEvent, label: string): string {
    switch (event.status) {
      case 'started':
        return `${label}: started`;
      case 'succeeded':
        return `${label}: complete`;
      case 'cancelled':
        return `${label}: cancelled`;
      case 'timed_out':
        return `${label}: timed out`;
      case 'failed': {
        const message = event.error?.message ?? 'unknown error';
        return `${label}: ${message}`;
      }
    }
  }
}
